import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const CONTENT = {
  "privacy-policy": {
    title: "Privacy Policy",
    updated: "Last updated: August 2026",
    sections: [
      { h: "Information We Collect", p: "When you purchase from Floxora, we collect only the details you provide at checkout — your name and phone number — along with basic technical data such as browser type and pages visited, which helps us improve the experience." },
      { h: "How We Use Your Information", p: "Your details are used solely to process your order, deliver your digital product access, and provide customer support. We may use aggregated, anonymous analytics to understand visitor behaviour on this page." },
      { h: "Cookies & Tracking", p: "We use cookies and similar technologies (including the Meta Pixel, where enabled) to measure advertising performance and understand how visitors interact with our page. You can disable cookies in your browser settings at any time." },
      { h: "Data Sharing", p: "We never sell, rent, or trade your personal information. Payment processing is handled securely by Razorpay; we never see or store your card or UPI details." },
      { h: "Data Security", p: "We apply industry-standard measures to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security." },
      { h: "Contact Us", p: "For any privacy-related questions or requests, reach us at support@floxora.in." },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: August 2026",
    sections: [
      { h: "Acceptance of Terms", p: "By accessing or purchasing from Floxora, you agree to these Terms & Conditions. If you do not agree, please do not use this website or purchase the product." },
      { h: "Product Description", p: "Floxora sells a digital mega bundle containing premium WordPress themes, pro plugins, a dropshipping course, and Shopify themes, delivered electronically via a Google Drive link after a successful one-time payment." },
      { h: "License & Usage", p: "Items in the bundle are distributed under the GNU General Public License (GPL) or their respective original licenses. You may use them on unlimited personal and client projects. You may not resell, redistribute, or repackage the bundle itself as your own product." },
      { h: "Payments", p: "All prices are listed in INR and processed securely through Razorpay. A purchase is complete only after successful payment confirmation." },
      { h: "Acceptable Use", p: "You agree not to misuse the website, attempt unauthorized access, or use the product for any unlawful purpose." },
      { h: "Limitation of Liability", p: "The bundle is provided 'as is' without warranties of any kind. Floxora shall not be liable for any indirect or consequential damages arising from the use of the products." },
      { h: "Changes to Terms", p: "We may update these terms from time to time. Continued use of the website after changes constitutes acceptance of the updated terms." },
      { h: "Contact", p: "Questions about these terms? Write to us at support@floxora.in." },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    updated: "Last updated: August 2026",
    sections: [
      { h: "General", p: "All information and products on this website are provided in good faith and for general informational and educational purposes only. Floxora makes no representation or warranty of any kind regarding accuracy, adequacy, or completeness." },
      { h: "No Earnings Guarantee", p: "The dropshipping course and related materials are educational resources. We do not guarantee any income, revenue, or business results. Your success depends entirely on your own effort, market conditions, and execution." },
      { h: "Third-Party Products", p: "Themes and plugins included in the bundle are developed by their respective original authors. Floxora is not affiliated with, endorsed by, or sponsored by WordPress, Shopify, or any plugin/theme developer unless explicitly stated." },
      { h: "Support Scope", p: "We provide access and basic setup guidance. Premium vendor support, license activation on vendor servers, or automatic vendor dashboards are not included." },
      { h: "External Links", p: "Our website may contain links to external sites (including Google Drive). We are not responsible for the content or practices of any third-party sites." },
      { h: "Contact", p: "For questions about this disclaimer, contact support@floxora.in." },
    ],
  },
  "refund-policy": {
    title: "Refund Policy",
    updated: "Last updated: August 2026",
    sections: [
      { h: "Digital Product Policy", p: "Due to the instantly accessible and downloadable nature of digital products, all sales are generally final once the access link has been delivered. We encourage you to read the product description carefully before purchasing." },
      { h: "When Refunds Are Considered", p: "A refund may be considered within 48 hours of purchase only in these cases: (1) you were charged twice for the same order, (2) the access link was not delivered and we could not resolve it within 72 hours, or (3) the delivered files are materially different from what was advertised." },
      { h: "Non-Refundable Cases", p: "Refunds are not provided for change of mind, incompatibility with your specific setup, failure to use the product, or results from applying the course material." },
      { h: "How to Request", p: "Email support@floxora.in with your Order ID and the reason for your request. Eligible refunds are processed to the original payment method within 5–7 business days after approval." },
      { h: "Chargebacks", p: "Please contact us before initiating a chargeback with your bank — most issues are resolved much faster directly. Unjustified chargebacks may lead to revocation of product access." },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const page = CONTENT[slug];

  if (!page) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center" data-testid="legal-not-found">
        <div className="text-center">
          <h1 className="font-['Outfit'] font-bold text-2xl mb-4">Page not found</h1>
          <Link to="/" className="text-[#A78BFA] underline text-sm" data-testid="legal-back-link">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={`legal-page-${slug}`}>
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo-text.png" alt="Floxora" className="h-7" />
          </div>
          <Link to="/" className="text-zinc-400 text-sm hover:text-[#A78BFA] transition-colors flex items-center gap-2" data-testid="legal-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-['Outfit'] font-extrabold tracking-tighter text-4xl sm:text-5xl mb-3">{page.title}</h1>
          <p className="text-zinc-400 text-xs mb-12">{page.updated}</p>
          <div className="space-y-10">
            {page.sections.map((s, i) => (
              <motion.section
                key={s.h}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                data-testid={`legal-section-${i}`}
              >
                <h2 className="font-['Outfit'] font-bold text-lg text-[#C4B5FD] mb-2">{s.h}</h2>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.p}</p>
              </motion.section>
            ))}
          </div>
          <div className="mt-16 border-t border-white/10 pt-8">
            <p className="text-zinc-400 text-xs">
              Questions? Contact us at{" "}
              <a href="mailto:support@floxora.in" className="text-[#A78BFA]" data-testid="legal-support-email">support@floxora.in</a>
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
